"use client";

import { useRef, useEffect, useCallback } from "react";
import { usePathname } from "next/navigation";

interface DiagnosticsHeroProps {
  savedCount?:   number;
  activeTab:     "new" | "saved";
  onTabChange:   (tab: "new" | "saved") => void;
  onQuerySubmit: (query: string) => void;
  queryLoading?: boolean;
  isAuthenticated?: boolean;
}

export function DiagnosticsHero({
  savedCount    = 0,
  activeTab,
  onTabChange,
  onQuerySubmit,
  queryLoading  = false,
  isAuthenticated = true,
}: DiagnosticsHeroProps) {
  const pathname = usePathname();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 200) + "px";
  }, []);

  useEffect(() => { autoResize(); }, [queryLoading, autoResize]);
  return (
    <section className="diag-hero">
      {/* Gradient icon */}
      <div className="diag-hero-icon" aria-hidden="true">
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <defs>
            <linearGradient id="hero-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#5B6CFF" />
              <stop offset="100%" stopColor="#EC4899" />
            </linearGradient>
          </defs>
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" stroke="url(#hero-grad)" />
        </svg>
      </div>

      <h1>
        <span className="grad-text">sensei.</span> Diagnostics
      </h1>
      <p className="diag-hero-sub">
        AI-powered supply chain maturity assessment and gap analysis
      </p>

      {/* Tabs */}
      <div className="diag-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === "new"}
          className={`diag-tab${activeTab === "new" ? " active" : ""}`}
          onClick={() => onTabChange("new")}
        >
          New Diagnostics
        </button>
        <button
          role="tab"
          aria-selected={activeTab === "saved"}
          className={`diag-tab${activeTab === "saved" ? " active" : ""}`}
          onClick={() => onTabChange("saved")}
        >
          Saved Diagnostics{" "}
          <span style={{ opacity: 0.6 }}>({savedCount})</span>
        </button>
      </div>

      {/* Ask bar + disclaimer — only shown on New tab */}
      {activeTab === "new" && (
        <>
          {!isAuthenticated && (
            <div style={{ maxWidth: "600px", margin: "16px auto 12px", padding: "0 24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", borderRadius: 16, border: "1px solid rgba(91,108,255,0.3)", background: "rgba(91,108,255,0.08)", width: "100%" }}>
                <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 12, background: "rgba(91,108,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }} aria-hidden="true">
                  🔒
                </div>
                <div style={{ flex: 1, textAlign: "left" }}>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>Sign in to run AI diagnostics</p>
                  <p style={{ margin: "2px 0 0", fontSize: 12, opacity: 0.7 }}>
                    Create a free account or sign in to generate and save diagnostic assessments.
                  </p>
                </div>
                <a
                  href={`/login?redirect_to=${encodeURIComponent(pathname)}`}
                  style={{ flexShrink: 0, padding: "8px 18px", borderRadius: 10, background: "linear-gradient(135deg,#5B6CFF,#EC4899)", color: "#fff", fontSize: 13, fontWeight: 600, textDecoration: "none" }}
                >
                  Sign in
                </a>
              </div>
            </div>
          )}
          <form
            className="ask-bar diag-ask-bar"
            onSubmit={(e) => {
              e.preventDefault();
              const ta = textareaRef.current;
              if (!ta) return;
              const val = ta.value.trim();
              if (val && !queryLoading) {
                onQuerySubmit(val);
                ta.value = "";
                ta.style.height = "auto";
              }
            }}
          >
            <textarea
              ref={textareaRef}
              name="query"
              rows={1}
              placeholder="Create me a supplier performance diagnostic…"
              aria-label="Ask SENSEI to create a diagnostic"
              disabled={queryLoading}
              onInput={autoResize}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  e.currentTarget.form?.requestSubmit();
                }
              }}
            />
            <button
              className="ask-send"
              type="submit"
              aria-label="Send"
              disabled={queryLoading}
            >
              {queryLoading ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ animation: "spin 1s linear infinite" }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              )}
            </button>
          </form>

          {/* Disclaimer */}
          <div className="ask-disclaimer" role="note">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ color: "var(--magenta)" }}>
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            AI SENSEI provides diagnostic insights — always validate findings with your team
          </div>
        </>
      )}
    </section>
  );
}
